import type { TFunction } from 'react-i18next';
import type { ValidIcons } from '@kizen/kds/Icon';
import type { AccessStruct } from '@kizen/permissions/types';
import { INTEGRATION_SECRETS_ROUTE } from '__pages/Settings/pages/ManageConnections/subroutes';
import {
  agenticWorkflowHref,
  dashboardHref,
  filterGroupHref,
  homepageHref,
  objectHref,
  smartConnectorHref,
  toolbarTemplateHref,
} from 'utility/assetHrefs';
import type {
  BusinessAppAsset,
  BusinessAppAssetType,
} from 'ts-components/Plugins/businessApps/types';

type AssetTypeConfig = {
  label: (t: TFunction) => string;
  icon: ValidIcons;
  href: (
    asset: BusinessAppAsset,
    clientObjectId?: string
  ) => string | undefined;
  canView?: (access: SettingsLinkAccess) => boolean;
};

export type SettingsLinkAccess = {
  team: { roles?: AccessStruct; permissionGroups?: AccessStruct };
  business: {
    businessInformation?: AccessStruct;
    businessCustomization?: AccessStruct;
  };
  canEditTeamMemberFields: boolean;
  manageConnections: { manageIntegrationSecrets?: AccessStruct };
};

export const ASSET_TYPES: [BusinessAppAssetType, AssetTypeConfig][] = [
  [
    'custom_object',
    {
      label: (t) => t('Custom Objects'),
      icon: 'asset-objects',
      href: ({ id }, clientObjectId) => objectHref(id, clientObjectId),
    },
  ],
  [
    'activity',
    {
      label: (t) => t('Activities'),
      icon: 'asset-activities',
      href: ({ id }) => `/activities/${id}`,
    },
  ],
  [
    'agentic_workflow',
    {
      label: (t) => t('Agentic Workflows'),
      icon: 'asset-automations',
      href: ({ id }) => agenticWorkflowHref(id),
    },
  ],
  [
    'smart_connector',
    {
      label: (t) => t('Smart Connectors'),
      icon: 'asset-smart-connectors',
      href: ({ id }) => smartConnectorHref(id),
    },
  ],
  [
    'dashboard',
    {
      label: (t) => t('Dashboards'),
      icon: 'asset-dashboards',
      href: ({ id }) => dashboardHref(id),
    },
  ],
  [
    'homepage',
    {
      label: (t) => t('Homepages'),
      icon: 'asset-homepages',
      href: ({ id }) => homepageHref(id),
    },
  ],
  [
    'chart_group',
    {
      label: (t) => t('Chart Groups'),
      icon: 'asset-chart-groups',
      href: ({ id, custom_object_id }, clientObjectId) =>
        custom_object_id &&
        `${objectHref(custom_object_id, clientObjectId)}/charts/${id}`,
    },
  ],
  [
    'form',
    {
      label: (t) => t('Forms'),
      icon: 'asset-forms',
      href: ({ id }) => `/forms/${id}`,
    },
  ],
  [
    'survey',
    {
      label: (t) => t('Surveys'),
      icon: 'asset-surveys',
      href: ({ id }) => `/surveys/${id}`,
    },
  ],
  [
    'filter_group',
    {
      label: (t) => t('Filter Groups'),
      icon: 'asset-filter-groups',
      href: ({ id, custom_object_id }, clientObjectId) =>
        custom_object_id &&
        filterGroupHref(id, custom_object_id, clientObjectId),
    },
  ],
  [
    'email_template',
    {
      label: (t) => t('Email Templates'),
      icon: 'asset-email-templates',
      href: ({ id }) => `/library/templates/${id}/build`,
    },
  ],
  [
    'toolbar_template',
    {
      label: (t) => t('Toolbar Templates'),
      icon: 'asset-toolbar-templates',
      href: ({ id }) => toolbarTemplateHref(id),
    },
  ],
  [
    'role',
    {
      label: (t) => t('Roles'),
      icon: 'asset-roles',
      href: () => '/settings/team/roles',
      canView: ({ team }) => Boolean(team.roles?.view),
    },
  ],
  [
    'permission_group',
    {
      label: (t) => t('Permission Groups'),
      icon: 'asset-roles',
      href: ({ id }) => `/settings/team/permission-groups?pg=${id}`,
      canView: ({ team }) => Boolean(team.permissionGroups?.view),
    },
  ],
  [
    'integration_secret',
    {
      label: (t) => t('Integration Secrets'),
      icon: 'custom-key',
      href: () => INTEGRATION_SECRETS_ROUTE,
      canView: ({ manageConnections: { manageIntegrationSecrets } }) =>
        Boolean(
          manageIntegrationSecrets?.view || manageIntegrationSecrets?.edit
        ),
    },
  ],
  [
    'business_variable',
    {
      label: (t) => t('Business Variables'),
      icon: 'custom-tag',
      href: () => '/settings/business',
      canView: ({ business }) =>
        Boolean(
          business.businessInformation?.edit ||
            business.businessCustomization?.edit
        ),
    },
  ],
  [
    'team_member_field',
    {
      label: (t) => t('Team Member Fields'),
      icon: 'custom-bars',
      href: ({ id }) => `/settings/custom-fields/teammembers/${id}`,
      canView: ({ canEditTeamMemberFields }) => canEditTeamMemberFields,
    },
  ],
];
